import { Box, IconButton, Menu, MenuItem } from "@mui/material";
import { makeStyles } from "@mui/styles";
import ContentShell from "components/ContentShell";
import SearchInput from "components/SearchInput";
import Table from "components/Table";
import ImagePreviewModal from "components/ImagePreviewModal";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { opacity2hex, sortArray } from "utils/miscUtils";
import configData from "config/config.json";
import { useSnackbar } from "hooks/useSnackbar";
import usePaginationHook from "hooks/usePagination";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { getSplashScreenTableColumns } from "utils/tableHelper/splashScreenTableHelper";
import sessionHelper from "services/sessionHelper";
import {
  archiveSplashScreens,
  copySplashScreen,
  deleteSplashScreen,
  getSplashScreens,
  unarchiveSplashScreens,
  updateSplashScreenStatus,
} from "services/splashScreenService";
import ArchiveActionBar from "components/archive/ArchiveActionBar";
import ArchiveViewButton from "components/archive/ArchiveViewButton";
import useContentArchive from "components/archive/useContentArchive";
import { PermittedRoles } from "utils/permissions";
import CustomButton from "components/CustomButton";
import useSearchHook from "hooks/useSearch";
import { defaultPageNo } from "utils/constants";
import DeleteConsentDialog from "components/DeleteConsentDialog";
import useConsent from "hooks/useConsent";

const useStyles = makeStyles((theme) => ({
  editButton: {
    backgroundColor: theme.palette.common.offWhite + opacity2hex("0.80"),
    color: "white",
    "&:hover": {
      backgroundColor: theme.palette.common.offWhite,
    },
  },
  container: {
    padding: "12px",
    border: "1px solid #ddd",
    borderRadius: "8px",
    backgroundColor: "#f9f9f9",
    [theme.breakpoints.down("sm")]: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
    },
  },
  previewButton: {
    backgroundColor: theme.palette.primary.main,
    color: "white",
    minWidth: "40px",
    height: "40px",
    borderRadius: "8px",
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
    "&:disabled": {
      backgroundColor: theme.palette.grey[400],
      color: theme.palette.grey[600],
    },
  },
}));

interface RowActionsProps {
  rowId?: any;
  data: any;
  actionHandlers: {
    handleDisble?: (id: any) => void;
    handleDelete?: (data: any) => void;
    handleCopySplashScreen?: (id: any) => void;
    handleArchive?: (ids: string[]) => void;
    archived?: boolean;
  };
}

interface ImagePreviewCellProps {
  data: any;
  onPreview: (imageUrl: string, title: string) => void;
}

const ImagePreviewCell = ({ data, onPreview }: ImagePreviewCellProps) => {
  const styles = useStyles();

  const handlePreview = (event: any) => {
    event.stopPropagation();
    onPreview(data?.imageUrl, data?.title || "Splash Screen Image");
  };

  return (
    <IconButton
      className={styles.previewButton}
      onClick={handlePreview}
      disabled={!data?.imageUrl}
      title={data?.imageUrl ? "Preview Image" : "No image available"}
    >
      <VisibilityIcon fontSize="small" />
    </IconButton>
  );
};

const RowActions = ({
  rowId,
  data,
  actionHandlers: {
    handleDisble = () => {},
    handleDelete = () => {},
    handleCopySplashScreen = () => {},
    handleArchive = () => {},
    archived = false,
  },
}: RowActionsProps) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClose = (event?: any) => {
    event?.stopPropagation?.();
    setAnchorEl(null);
  };

  const handleClick = (event: any) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleRowAction = (event: any, data: any) => {
    handleClose(event);
    navigate(configData.routes.splashScreen.edit?.replace(":id", data?._id));
  };

  const handleDisbleClick = (event: any, id: any) => {
    handleClose(event);
    handleDisble(id);
  };

  const handleCopySplashScreenClick = (event: any, id: any) => {
    handleClose(event);
    handleCopySplashScreen(id);
  };

  const handleDeleteClick = (event: any, rowData: any) => {
    handleClose(event);
    handleDelete(rowData);
  };

  const hasDeletePermission = sessionHelper.hasRole(
    PermittedRoles.SPLASH_SCREEN.DELETE_ROLES,
  );

  return (
    <>
      <IconButton onClick={handleClick}>
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        {archived ? (
          <MenuItem
            onClick={(e) => {
              handleClose(e);
              handleArchive([data?._id]);
            }}
          >
            Restore
          </MenuItem>
        ) : (
          [
            <MenuItem key="edit" onClick={(e) => handleRowAction(e, data)}>
              Edit
            </MenuItem>,
            <MenuItem
              key="status"
              onClick={(e) => handleDisbleClick(e, data?._id)}
            >
              {data?.isActive ? "Disable" : "Enable"}
            </MenuItem>,
            <MenuItem
              key="copy"
              onClick={(e) => handleCopySplashScreenClick(e, data?._id)}
            >
              Copy
            </MenuItem>,
            <MenuItem
              key="archive"
              disabled={data?.isActive}
              onClick={(e) => {
                handleClose(e);
                handleArchive([data?._id]);
              }}
            >
              Archive
            </MenuItem>,
            hasDeletePermission ? (
              <MenuItem
                key="delete"
                onClick={(e) => handleDeleteClick(e, data)}
              >
                Delete
              </MenuItem>
            ) : null,
          ]
        )}
      </Menu>
    </>
  );
};

const SplashScreen = () => {
  const styles = useStyles();
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [columns, setColumns] = useState([]);
  const [listData, setListData] = useState([]);
  const { searchValue, onSearch: _onSearch } = useSearchHook();
  const [orderDirection, setOrderDirection] = useState("asc");
  const [imagePreview, setImagePreview] = useState({
    open: false,
    imageUrl: "",
    title: "",
  });

  const {
    isOpenConsent,
    constentDetails,
    openConsent,
    closeConsent,
    updateDetails,
  } = useConsent();

  const onSearch = (value: any) => {
    // reset filters if any
    onChangePage(defaultPageNo);
    _onSearch(value);
  };

  const hasEditPermission = sessionHelper.hasRole(
    PermittedRoles.SPLASH_SCREEN.UPDATE_ROLES,
  );

  const createSplashScreen = () =>
    navigate(configData.routes.splashScreen.create);

  const { onChangePage, onPageSizeChange, pageNo, pageSize } =
    usePaginationHook({ totalItems: listData?.length });

  const archive = useContentArchive({
    label: { singular: "splash screen", plural: "splash screens" },
    isLive: (row: any) => !!row?.isActive,
    archive: archiveSplashScreens,
    unarchive: unarchiveSplashScreens,
    onCompleted: () => fetchData(),
    onViewChange: () => onChangePage(defaultPageNo),
  });

  // Archived records are a read-only view, so their rows do not link into the editor.
  const rowHref = (data: any) =>
    hasEditPermission && !archive.archived
      ? configData.routes.splashScreen.edit?.replace(":id", data?._id)
      : null;

  const onColumnClick = (column: any) => {
    let _list = sortArray([...listData], orderDirection, column);
    setListData(_list);
    setOrderDirection(orderDirection === "asc" ? "desc" : "asc");
  };

  const onDelete = async () => {
    try {
      const resp = await deleteSplashScreen(constentDetails?.id);
      showSnackbar(resp?.data?.message);
      fetchData();
      closeConsent();
    } catch (error) {
      closeConsent();
      showSnackbar(error.message, "error");
    }
  };

  const onCopy = async (id: any) => {
    try {
      setLoading(true);
      const resp = await copySplashScreen(id);
      await fetchData();
      showSnackbar(resp?.message);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      showSnackbar(error.message, "error");
    }
  };

  const _openConsentDialog = (data: any) => {
    openConsent();
    updateDetails({ itemName: data?.title ?? data?._id, id: data?._id });
  };

  const handleImagePreview = (imageUrl: string, title: string) => {
    setImagePreview({
      open: true,
      imageUrl,
      title,
    });
  };

  const handleCloseImagePreview = () => {
    setImagePreview({
      open: false,
      imageUrl: "",
      title: "",
    });
  };

  const onDisable = async (splashScreenId) => {
    try {
      const resp = await updateSplashScreenStatus(splashScreenId);
      showSnackbar(resp?.data?.message);
      fetchData();
    } catch (error) {
      showSnackbar(error.message, "error");
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      let payload: any = { pageNo, pageSize, archived: archive.archived };
      if (searchValue) payload["search"] = searchValue;
      const resp = await getSplashScreens(payload);

      setListData(
        resp?.data?.items?.map((item) => ({
          ...item,
          isActiveText: item.isActive ? "YES" : "NO",
          isTitleBoldText: item.isTitleBold ? "YES" : "NO",
          isBodyBoldText: item.isBodyBold ? "YES" : "NO",
          order: item.order ?? "-",
          targetedUsersCount:
            item.targetedUsers && item.targetedUsers.length > 0
              ? item.targetedUsers.length
              : "All Users",
          rowDisabled: !item?.isActive,
          copiedFrom: item?.copiedFrom || "-",
          preview: (
            <ImagePreviewCell data={item} onPreview={handleImagePreview} />
          ),
        })) || [],
      );
      setLoading(false);
    } catch (error) {
      setLoading(false);
      showSnackbar(error.message, "error");
    }
  };

  useEffect(() => {
    const cols = getSplashScreenTableColumns().filter(
      (col) => col.id !== "actions" || hasEditPermission,
    );
    setColumns(cols);
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNo, pageSize, searchValue, hasEditPermission, archive.archived]);

  return (
    <ContentShell
      title={archive.archived ? "Archived Splash Screens" : "Splash Screen"}
      loading={loading}
      actions={
        <Box style={{ display: "flex", flexFlow: "row nowrap", gap: 4 }}>
          <SearchInput
            value={searchValue}
            placeholder="Search a splash screen"
            onSearch={onSearch}
          />
          <ArchiveViewButton archive={archive} />
          {!archive.archived &&
            sessionHelper.hasRole(
              PermittedRoles.SPLASH_SCREEN.CREATE_ROLES,
            ) && (
              <CustomButton onClick={createSplashScreen}>Create</CustomButton>
            )}
        </Box>
      }
    >
      <Box className={styles.container}>
        <ArchiveActionBar archive={archive} rows={listData} nameKey="title" />
        <Table
          columns={columns}
          data={listData}
          currentPage={pageNo}
          pageSize={pageSize}
          totalEntries={listData.length}
          highlightAltRow={true}
          actions={RowActions}
          onChangePage={onChangePage}
          onRowsPerPageChange={onPageSizeChange}
          onColumnClick={onColumnClick}
          rowHref={rowHref}
          selectable
          selectedIds={archive.selectedIds}
          onSelectionChange={archive.setSelectedIds}
          rowSelectDisabledReason={archive.rowSelectDisabledReason}
          actionHandlers={{
            handleDisble: onDisable,
            handleDelete: _openConsentDialog,
            handleCopySplashScreen: onCopy,
            handleArchive: archive.requestAction,
            archived: archive.archived,
          }}
        />
        <DeleteConsentDialog
          itemName={constentDetails?.itemName}
          open={isOpenConsent}
          onClose={closeConsent}
          onConfirm={onDelete}
        />
        <ImagePreviewModal
          open={imagePreview.open}
          onClose={handleCloseImagePreview}
          imageUrl={imagePreview.imageUrl}
          title={imagePreview.title}
          alt={`Preview of ${imagePreview.title}`}
        />
      </Box>
    </ContentShell>
  );
};

export default SplashScreen;
